import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '../config/firebase';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      await auth.authStateReady();
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      const data: any = error.response.data;

      // 503 Banking Hub desativado — suprimir log (intencional, aguarda BCV)
      const isBankingHubDisabled = status === 503 &&
        typeof data?.error === 'string' &&
        (data.error.includes('Banking Hub') || data.error.includes('Banco Central'));
      if (isBankingHubDisabled) {
        throw new Error(data.error);
      }

      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          console.error('Unauthorized access - redirecting to login');
          // You can dispatch a logout action here if needed
          break;
        case 403:
          console.error('Forbidden access');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error:', data.error, data.details || '');
          break;
        default:
          console.error('API error:', data.error);
      }

      // O erro leva a resposta original agarrada.
      //
      // Antes lançava-se um `Error` simples: a mensagem do servidor ia em
      // `.message`, mas `.response` desaparecia. E 49 das 76 páginas do admin
      // procuram exactamente `err.response.data.error` — ficavam todas a mostrar
      // um texto genérico em vez da razão real. Foi assim que o ecrã das taxas de
      // carregamento disse «confirme que existe um contrato activo» quando o
      // servidor tinha explicado que havia DOIS e que não ia adivinhar entre eles.
      //
      // Preservar em vez de traduzir: quem ler `.message` continua a funcionar, e
      // quem ler `.response.data.error` passa a funcionar.
      const erro: any = new Error(data.error || 'An error occurred');
      erro.response = error.response;
      erro.status = status;
      throw erro;
    } else if (error.request) {
      // Request made but no response
      console.error('Network error - no response received');
      throw new Error('Network error - please check your connection');
    } else {
      // Error setting up request
      console.error('Request error:', error.message);
      throw error;
    }
  }
);

export default api;

// Export API base URL for direct access
export { API_URL, API_VERSION };
